import { useState, useRef, useEffect } from 'react';
import { Calendar } from 'lucide-react';

// Converts YYYY-MM-DD → DD/MM/YYYY for display
function isoToDisplay(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return '';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

// Converts DD/MM/YYYY → YYYY-MM-DD for the value prop
function displayToIso(display) {
  const m = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}

// Auto-insert slashes as the user types: "27" → "27/", "27/05" → "27/05/"
function autoFormat(prev, next) {
  // Strip non-digits
  const digits = next.replace(/\D/g, '').slice(0, 8);
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) out += '/';
    out += digits[i];
  }
  return out;
}

function DateInput({ value, onChange, className, placeholder = 'dd/mm/yyyy', required, tabIndex }) {
  const [display, setDisplay] = useState(isoToDisplay(value));
  const pickerRef = useRef(null);

  // Sync display when value changes externally (e.g. form reset)
  useEffect(() => {
    setDisplay(isoToDisplay(value));
  }, [value]);

  const handleText = (e) => {
    const formatted = autoFormat(display, e.target.value);
    setDisplay(formatted);
    const iso = displayToIso(formatted);
    if (iso) onChange({ target: { value: iso } });
    else if (!formatted) onChange({ target: { value: '' } });
  };

  // Native date picker changed → update both ISO value and display text
  const handlePicker = (e) => {
    onChange(e);
    setDisplay(isoToDisplay(e.target.value));
  };

  const openPicker = () => {
    if (!pickerRef.current) return;
    if (typeof pickerRef.current.showPicker === 'function') {
      try { pickerRef.current.showPicker(); } catch { pickerRef.current.click(); }
    } else {
      pickerRef.current.click();
    }
  };

  return (
    <div className="relative flex items-center">
      <input
        type="text"
        value={display}
        onChange={handleText}
        placeholder={placeholder}
        className={`${className} pr-8`}
        required={required}
        tabIndex={tabIndex}
        maxLength={10}
      />
      {/* Hidden native date picker — opened programmatically by the icon button */}
      <input
        ref={pickerRef}
        type="date"
        value={value || ''}
        onChange={handlePicker}
        tabIndex={-1}
        className="absolute right-0 w-0 h-0 opacity-0 pointer-events-none"
      />
      {/* Calendar icon button */}
      <button
        type="button"
        onClick={openPicker}
        tabIndex={-1}
        className="absolute right-2 text-gray-400 hover:text-purple-600 transition-colors"
      >
        <Calendar size={15} />
      </button>
    </div>
  );
}

export default DateInput;
